import { useState } from "react";
import { PackageIcon, PlusIcon } from "@primer/octicons-react";
import { ActionList, ActionMenu } from "@primer/react";
import { Alert } from "../components/Alert/Alert.jsx";
import { AssetDecorations } from "../components/AssetDecorations/AssetDecorations.jsx";
import { AssetIdentity, assetHref } from "../components/AssetIdentity/AssetIdentity.jsx";
import { Button } from "../components/Button/Button.jsx";
import { ComplianceImpact } from "../components/ComplianceMetric/ComplianceMetric.jsx";
import { ListNoResults, ListSearch } from "../components/ListSearch/ListSearch.jsx";
import { EmptyState } from "../components/EmptyState/EmptyState.jsx";
import { PageHeading } from "../components/PageHeading/PageHeading.jsx";
import { RecordList } from "../components/RecordList/RecordList.jsx";
import { SampleRecordAction } from "../components/SampleRecordAction/SampleRecordAction.jsx";
import { effectiveValue } from "../domain/document.js";
import { assetComplianceImpacts, evaluateCompliance } from "../domain/compliance.js";
import { createSampleAsset, findSampleClass } from "../domain/sampleRecords.js";
import { navigateToHash } from "../lib/navigateToHash.js";
import { useApplicationDocument } from "../storage/useApplicationDocument.js";
import { usePresentationState } from "../storage/presentationState.js";
import "./AssetsPage.css";

export function AssetsPage({ profileId: profileIdOverride, repository }) {
  const application = useApplicationDocument(repository);
  const [search, setSearch] = useState("");
  const [decorationPropertyIds, setDecorationPropertyIds] = usePresentationState({
    defaultValue: [], lifetime: "persistent", name: "decorations", page: "assets", profileId: application.profile?.id ?? profileIdOverride, scope: "profile", validate: stringArray
  });
  const [sort, setSort] = usePresentationState({
    defaultValue: "name", lifetime: "persistent", name: "sort", page: "assets", profileId: application.profile?.id ?? profileIdOverride, scope: "profile", validate: stringValue
  });

  async function addSampleAsset() {
    const nextDocument = createSampleAsset(application.document);
    if (await application.save(nextDocument)) navigateToHash(assetHref(nextDocument.assets.at(-1).id));
  }

  if (application.error && !application.document) return <Alert tone="danger" heading="Unable to open Regulator">{application.error.message}</Alert>;
  if (!application.document) return <AssetsLoading />;

  const assets = application.document.assets;
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const filteredAssets = assets.filter((asset) => (
    !normalizedSearch
    || asset.id.toLocaleLowerCase().includes(normalizedSearch)
    || asset.name.toLocaleLowerCase().includes(normalizedSearch)
  ));
  const canAddSample = assets.length === 0 && Boolean(findSampleClass(application.document));
  const decorationOptions = [
    ...application.document.properties.map((property) => ({ id: `property:${property.id}`, label: property.name, propertyId: property.id })),
    { id: "allocation", label: "Allocation" }
  ];
  const selectedDecorationIds = decorationOptions
    .filter((option) => decorationPropertyIds.includes(option.id) || (option.propertyId && decorationPropertyIds.includes(option.propertyId)))
    .map((option) => option.id);
  const visibleDecorationPropertyIds = decorationOptions.filter((option) => option.propertyId && selectedDecorationIds.includes(option.id)).map((option) => option.propertyId);
  const sortOptions = [
    { id: "name", label: "Asset name" },
    ...application.document.properties.map((property) => ({ id: `property:${property.id}`, label: property.name })),
    { id: "allocation", label: "Allocation" }
  ];
  const selectedSort = sortOptions.some((option) => option.id === sort) ? sort : "name";
  const visibleAssets = sortAssets(application.document, filteredAssets, selectedSort);
  const impacts = assetComplianceImpacts(application.document, evaluateCompliance(application.document));

  return <article className="assets-page">
    <PageHeading description="Manage the investments included in this portfolio." actions={<><SortMenu onChange={setSort} options={sortOptions} selectedId={selectedSort} /><DecorationsMenu onChange={setDecorationPropertyIds} options={decorationOptions} selectedIds={selectedDecorationIds} /><Button as="a" href="#/assets/new" leadingVisual={PlusIcon}>Add asset</Button></>}>Assets</PageHeading>
    {application.error ? <Alert tone="danger" heading="Changes were not saved">{application.error.message}</Alert> : null}
    {assets.length === 0 ? <AssetsEmpty onAddSample={canAddSample ? addSampleAsset : null} saving={application.saving} /> : <>
      <ListSearch ariaLabel="Search assets" clearLabel="Clear asset search" placeholder="Search by name or ticker" value={search} visibleCount={visibleAssets.length} totalCount={assets.length} onChange={(event) => setSearch(event.target.value)} onClear={() => setSearch("")} />
      {visibleAssets.length ? <RecordList className="assets-list">{visibleAssets.map((asset) => {
        const impact = impacts.get(asset.id);
        const metric = <ComplianceImpact impact={impact} />;
        return <div className="record-row asset-item" key={asset.id}><div className="asset-summary"><AssetIdentity asset={asset} href={assetHref(asset.id)} trailing={metric} /><AssetDecorations allocationBasisPoints={selectedDecorationIds.includes("allocation") ? assetAllocation(application.document, asset.id) : null} assetId={asset.id} document={application.document} propertyIds={visibleDecorationPropertyIds} /></div></div>;
      })}</RecordList> : <ListNoResults heading="No matching assets">Try another name or ticker.</ListNoResults>}
    </>}
  </article>;
}

function SortMenu({ onChange, options, selectedId }) {
  const selectedOption = options.find((option) => option.id === selectedId);
  return <ActionMenu>
    <ActionMenu.Button>Sort: {selectedOption.label}</ActionMenu.Button>
    <ActionMenu.Overlay><ActionList selectionVariant="single">{options.map((option) => <ActionList.Item key={option.id} selected={option.id === selectedId} onSelect={() => onChange(option.id)}>{option.label}</ActionList.Item>)}</ActionList></ActionMenu.Overlay>
  </ActionMenu>;
}

function DecorationsMenu({ onChange, options, selectedIds }) {
  const selected = new Set(selectedIds);

  function toggleDecoration(event, decorationId) {
    event.preventDefault();
    onChange(options.filter((option) => option.id === decorationId ? !selected.has(option.id) : selected.has(option.id)).map((option) => option.id));
  }

  return <ActionMenu>
    <ActionMenu.Button>Decorations{selected.size ? ` (${selected.size})` : ""}</ActionMenu.Button>
    <ActionMenu.Overlay><ActionList selectionVariant="multiple">{options.map((option) => <ActionList.Item key={option.id} selected={selected.has(option.id)} onSelect={(event) => toggleDecoration(event, option.id)}>{option.label}</ActionList.Item>)}</ActionList></ActionMenu.Overlay>
  </ActionMenu>;
}

function stringArray(value) {
  return Array.isArray(value) && value.every((item) => typeof item === "string") ? value : null;
}

function stringValue(value) {
  return typeof value === "string" ? value : null;
}

function sortAssets(document, assets, sort) {
  const comparedAssets = [...assets];
  if (sort === "allocation") return comparedAssets.sort((left, right) => assetAllocation(document, right.id) - assetAllocation(document, left.id) || compareAssetNames(left, right));
  if (sort.startsWith("property:")) {
    const property = document.properties.find((item) => item.id === sort.slice("property:".length));
    if (property) return comparedAssets.sort((left, right) => propertyValueIndex(document, property, left.id) - propertyValueIndex(document, property, right.id) || compareAssetNames(left, right));
  }
  return comparedAssets.sort(compareAssetNames);
}

function propertyValueIndex(document, property, assetId) {
  return property.values.findIndex((value) => value.id === effectiveValue(document, assetId, property.id).id);
}

function compareAssetNames(left, right) {
  return (left.name || left.id).localeCompare(right.name || right.id, undefined, { numeric: true, sensitivity: "base" }) || left.id.localeCompare(right.id, undefined, { numeric: true, sensitivity: "base" });
}

function assetAllocation(document, assetId) {
  return (document.allocations ?? []).find((allocation) => allocation.assetId === assetId)?.basisPoints ?? 0;
}

function AssetsEmpty({ onAddSample, saving }) {
  return <EmptyState
    icon={PackageIcon}
    heading="No assets yet"
    description="Add the investments you want Regulator to classify and check."
    action={<Button as="a" href="#/assets/new" leadingVisual={PlusIcon}>Add your first asset</Button>}
    secondaryAction={onAddSample ? <SampleRecordAction disabled={saving} onSelect={onAddSample}>add SPY as a sample asset</SampleRecordAction> : null}
  />;
}

function AssetsLoading() {
  return <article className="assets-page"><PageHeading description="Loading assets…">Assets</PageHeading></article>;
}
